import React, { useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useCompleteOAuthLoginMutation } from "@/store/rest_api";
import { useAuth } from "@/hooks/useAuth";
import { authService } from "@/services/authService";
import { apiErrorMessage } from "@/util/apiError";
import { DEVICE_PLATFORM, getDeviceName, getDeviceToken, setDeviceToken } from "@/util/device";
import {
  OAUTH_LOGIN_REDIRECT_KEY,
  OAUTH_LOGIN_STATE_KEY,
} from "@/components/OAuthLoginButtons";
import { Button } from "@/components/ui/button";

interface ErrorView {
  title: string;
  description: string;
  requestAccess?: boolean;
}

function resolveError(code: string): ErrorView {
  switch (code) {
    case "invite_required":
      return {
        title: "You need an invite",
        description:
          "Retreevo is invite-only right now. Request access and we'll email you a link to finish signing up with your CRM.",
        requestAccess: true,
      };
    case "email_not_allowed":
      return {
        title: "Use a permanent email",
        description: "Please sign in with a CRM account that uses a permanent work email address.",
      };
    case "invite_email_mismatch":
      return {
        title: "Email doesn't match your invite",
        description: "Sign in with the CRM account whose email matches the address your invite was sent to.",
      };
    case "email_unavailable":
      return {
        title: "No email from your CRM",
        description:
          "Your CRM didn't share an email address. Create your account with email first, then link your CRM from settings.",
      };
    default:
      return {
        title: "We couldn't sign you in",
        description: "Something went wrong completing sign-in. Please try again.",
      };
  }
}

export default function OAuthLoginCallback() {
  const navigate = useNavigate();
  const { provider } = useParams();
  const { refreshAuthStatus } = useAuth();
  const [searchParams] = useSearchParams();
  const [completeOAuthLogin, result] = useCompleteOAuthLoginMutation();
  const startedRef = useRef(false);

  const providerError = searchParams.get("error");
  const stateMismatch = useMemo(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const expected = sessionStorage.getItem(OAUTH_LOGIN_STATE_KEY);
    return !code || !state || !expected || state !== expected;
  }, [searchParams]);

  useEffect(() => {
    if (startedRef.current || providerError || stateMismatch || !provider) {
      return;
    }
    startedRef.current = true;
    completeOAuthLogin({
      provider,
      code: searchParams.get("code")!,
      state: searchParams.get("state")!,
      deviceToken: getDeviceToken() ?? undefined,
      deviceName: getDeviceName(),
      devicePlatform: DEVICE_PLATFORM,
    });
  }, [provider, providerError, stateMismatch, searchParams, completeOAuthLogin]);

  useEffect(() => {
    if (!result.isSuccess) {
      return;
    }
    const data = result.data;
    if ("twoFactorRequired" in data) {
      navigate(data.enrollmentRequired ? "/login/enroll" : "/login/verify", { state: data });
      return;
    }
    authService.setAccessToken(data.accessToken);
    authService.setRefreshToken(data.refreshToken);
    if (data.deviceToken) {
      setDeviceToken(data.deviceToken);
    }
    const redirect = sessionStorage.getItem(OAUTH_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(OAUTH_LOGIN_STATE_KEY);
    sessionStorage.removeItem(OAUTH_LOGIN_REDIRECT_KEY);
    refreshAuthStatus().then(() => navigate(redirect || "/dashboard"));
  }, [result, navigate, refreshAuthStatus]);

  const failed = Boolean(providerError) || stateMismatch || result.isError;

  if (result.isSuccess || !failed) {
    return (
      <div className="w-full max-w-md space-y-6 text-foreground">
        <div className="rounded-xl border bg-card shadow-sm p-8 flex flex-col items-center gap-3 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Signing you in...</p>
        </div>
      </div>
    );
  }

  const code = result.isError
    ? apiErrorMessage(result.error, "")
    : providerError
      ? "provider_error"
      : "invalid_state";
  const view = resolveError(code);

  return (
    <div className="w-full max-w-md space-y-6 text-foreground">
      <div className="rounded-xl border bg-card shadow-sm p-8 space-y-4 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">{view.title}</h1>
        <p className="text-sm text-muted-foreground">{view.description}</p>
        {view.requestAccess && (
          <Button asChild className="w-full">
            <Link to="/request-access">Request access</Link>
          </Button>
        )}
        <div className="text-sm text-muted-foreground">
          <Link to="/login" className="font-medium text-foreground hover:text-primary">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
